import { useState } from "react";
import { MousePointerClick, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import { getReactionRoles, addReactionRole, deleteReactionRole } from "@/api/reactionRoles";
import { CrudCard, ListRow, IconButton } from "./CrudCard";
import { Modal } from "./Modal";
import { showApiError } from "./helpers";
import { Field, TextInput, ChannelSelect, RoleSelect, ChoiceSelect, ToggleRow } from "./fields";

const MODES = ["toggle", "once", "addonly", "removeonly"];
const EMPTY_FORM = { message_id: "", channel_id: "", role_id: "", emoji: "", mode: "toggle", remove_on_unreact: true };

export const ReactionRoles = ({ guildId, roles, channels }) => {
  const { data, loading, error, refetch } = useApi(() => getReactionRoles(guildId));
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const items = data?.reaction_roles || [];
  const roleName = (id) => roles.find((r) => String(r.id) === String(id))?.name || `Role ${id}`;
  const channelName = (id) => {
    const ch = channels.find((c) => String(c.id) === String(id));
    return ch ? `#${ch.name}` : `Channel ${id}`;
  };
  const selectableRoles = roles.filter((r) => !r.managed && r.name !== "@everyone");

  const submit = async () => {
    const { form } = modal;
    if (!/^\d+$/.test(String(form.message_id).trim())) {
      toast.error("Message ID must be a numeric Discord message ID.");
      return;
    }
    if (!form.channel_id || !form.role_id || !form.emoji.trim()) {
      toast.error("Channel, role, and emoji are all required.");
      return;
    }
    setSaving(true);
    try {
      await addReactionRole(guildId, { ...form, message_id: String(form.message_id).trim(), emoji: form.emoji.trim() });
      toast.success("Reaction role added.");
      setModal(null);
      await refetch();
    } catch (e) {
      showApiError(e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    try {
      await deleteReactionRole(guildId, item.message_id, item.role_id);
      toast.success("Reaction role removed.");
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  return (
    <>
      <CrudCard
        icon={MousePointerClick}
        title="Reaction Roles"
        description="Members self-assign roles by reacting to a message."
        testId="crud-reaction-roles"
        id="reaction-roles"
        loading={loading}
        error={error}
        onRetry={refetch}
        isEmpty={items.length === 0}
        emptyText="No reaction roles configured."
        addLabel="New reaction role"
        onAdd={() => setModal({ form: { ...EMPTY_FORM } })}
      >
        <ul className="space-y-3">
          {items.map((item) => (
            <ListRow
              key={`${item.message_id}-${item.role_id}`}
              testId={`reaction-role-row-${item.message_id}-${item.role_id}`}
              actions={
                <IconButton onClick={() => remove(item)} label="Delete" danger testId={`reaction-role-delete-${item.message_id}-${item.role_id}`}>
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </IconButton>
              }
            >
              <p className="text-sm text-paper-100">
                {item.emoji} → <span className="font-semibold">{roleName(item.role_id)}</span>
              </p>
              <p className="mt-0.5 font-mono text-xs text-paper-500">
                {channelName(item.channel_id)} · msg {item.message_id} · {item.mode || "toggle"}
                {item.remove_on_unreact ? " · removes on unreact" : ""}
              </p>
            </ListRow>
          ))}
        </ul>
      </CrudCard>

      <Modal open={!!modal} onClose={() => setModal(null)} title="New reaction role" testId="reaction-role-modal">
        {modal && (
          <div className="space-y-4">
            <Field label="Message ID" hint="Right-click a message in Discord → Copy Message ID (developer mode).">
              <TextInput value={modal.form.message_id} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, message_id: v } }))} placeholder="1234567890123456789" testId="rr-form-message" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Channel">
                <ChannelSelect value={modal.form.channel_id} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, channel_id: v } }))} channels={channels} types={["text", "news"]} testId="rr-form-channel" />
              </Field>
              <Field label="Role">
                <RoleSelect value={modal.form.role_id} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, role_id: v } }))} roles={selectableRoles} testId="rr-form-role" />
              </Field>
              <Field label="Emoji">
                <TextInput value={modal.form.emoji} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, emoji: v } }))} placeholder="✅" testId="rr-form-emoji" />
              </Field>
              <Field label="Mode">
                <ChoiceSelect value={modal.form.mode} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, mode: v } }))} options={MODES} testId="rr-form-mode" />
              </Field>
            </div>
            <ToggleRow label="Remove role when reaction is removed" checked={modal.form.remove_on_unreact} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, remove_on_unreact: v } }))} testId="rr-form-remove-unreact" />
            <button
              onClick={submit}
              disabled={saving}
              data-testid="rr-form-save"
              className="w-full rounded-full bg-white py-3 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.01] disabled:opacity-50"
            >
              {saving ? "Adding…" : "Add reaction role"}
            </button>
          </div>
        )}
      </Modal>
    </>
  );
};
